import { Link } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import HaveAQuestionBadge from "@/components/HaveAQuestionBadge";

// Terms of Use page. Locks in the policy points the user requested in iter-23:
// - 12-month data retention after deactivation
// - No age requirement
// - Creating an account = agreement to ToS
// - Export available at standard rates
// - Organization name: ThatGantt
//
// Tone is still witty but the legal content is structured. We're not
// pretending this is a law-firm-drafted document — it's a plain-English
// summary that links to a contact path for anything else.
export default function Terms() {
  const updated = "February 14, 2026";
  return (
    <div className="min-h-screen tg-public-bg">
      <PublicNav />

      <article className="mx-auto max-w-[840px] px-4 pt-16 pb-24">
        <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-3">
          Legal · last updated {updated}
        </div>
        <h1 className="text-5xl md:text-6xl tracking-tight leading-[1.05] text-slate-950">
          Terms of <span className="tg-gradient-text">Use.</span>
        </h1>
        <p className="mt-5 text-base text-slate-600 leading-relaxed">
          These terms govern your use of ThatGantt (the "Service"), operated by
          ThatGantt. They're written in plain English on purpose — if anything
          here surprises you, email us. By creating an account, importing a
          project, or even just opening a shared chart, you agree to the
          following. Yes, even the boring bits.
        </p>

        <Section
          n="01"
          title="The Service"
          body={
            <>
              <p>
                ThatGantt is a project-timeline tool that lets you track tasks,
                subtasks, dependencies, and multiple dates per task; collaborate
                with teammates via project distribution lists; and export your
                data. We charge for some tiers, listed on the{" "}
                <Link to="/pricing" className="text-indigo-600 underline hover:text-indigo-800">Pricing page</Link>.
              </p>
              <p>
                We don't promise the Service will be uninterrupted or
                bug-free, but we try really hard. When something breaks we fix
                it — usually within the week. Sometimes within the hour, if it's
                clearly on fire.
              </p>
            </>
          }
        />

        <Section
          n="02"
          title="Your account"
          body={
            <>
              <p>
                <strong>No age minimum.</strong> If you can run a project, you
                can run an account.
              </p>
              <p>
                <strong>Creating an account is your acceptance of these terms.</strong>{" "}
                It's also your acceptance of our service policies — same thing,
                different sentence.
              </p>
              <p>
                You're responsible for what happens under your login. Don't
                share passwords. Use the share-by-email feature instead; it
                exists for exactly that reason.
              </p>
            </>
          }
        />

        <Section
          n="03"
          title="Your data"
          body={
            <>
              <p>
                <strong>While you have an account</strong>, we preserve all
                relevant data — projects, tasks, comments, custom columns,
                activity history. Forever-ish. Until you tell us otherwise.
              </p>
              <p>
                <strong>If you deactivate your account</strong>, we keep your
                data for{" "}
                <span className="font-semibold text-slate-950">12 months</span>{" "}
                in case you change your mind. After that, we purge it — backups
                included. Reactivation within the 12-month window restores
                everything; after that, we can't help you.
              </p>
              <p>
                <strong>Export</strong>. The Service includes a built-in export
                feature (PDF / PNG / CSV / XLSX). Standard pricing for your tier
                applies — meaning Free tier users can export from any of their
                10 projects; paid tiers export unlimited. No "data hostage" fees.
                Ever.
              </p>
            </>
          }
        />

        <Section
          n="04"
          title="Acceptable use"
          body={
            <>
              <p>Don't do illegal things with the Service. Don't:</p>
              <ul className="list-disc pl-5 space-y-1.5 text-slate-700">
                <li>Reverse-engineer the app or scrape it at scale</li>
                <li>Upload content that isn't yours (we'll honor DMCA takedowns)</li>
                <li>Use it to spam, phish, or harass</li>
                <li>Try to break our security boundaries (see our{" "}
                  <Link to="/contact" className="text-indigo-600 underline hover:text-indigo-800">contact page</Link>{" "}
                  for our responsible-disclosure policy)
                </li>
              </ul>
              <p>If you do any of these, we reserve the right to suspend your
                account without notice and, in serious cases, hand evidence to
                law enforcement.</p>
            </>
          }
        />

        <Section
          n="05"
          title="Payment, refunds, cancellation"
          body={
            <>
              <p>
                Paid tiers are billed monthly in advance. You can cancel any
                time from your account settings; access continues until the end
                of the billing period.
              </p>
              <p>
                Refunds: we don't routinely issue them for partial months, but
                if something specific went wrong (a billing error, a feature
                we said we shipped and didn't), email{" "}
                <Link to="/contact" className="text-indigo-600 underline hover:text-indigo-800">use the contact form</Link>{" "}
                and we'll work it out.
              </p>
            </>
          }
        />

        <Section
          n="06"
          title="Intellectual property"
          body={
            <p>
              You own your project data, your task names, your custom columns,
              everything you put in. We own the Service itself — the app, the
              brand "ThatGantt," the favicon, the witty copy. Don't copy it;
              we'll be sad and possibly litigious.
            </p>
          }
        />

        <Section
          n="07"
          title="Liability"
          body={
            <p>
              The Service is provided "as-is." We do our best, but we're not
              liable for indirect, incidental, or consequential damages
              (lost profits, lost data, lost weekends, lost dignity in front
              of your VP). To the extent allowed by law, our maximum
              liability is the amount you paid us in the prior 12 months.
            </p>
          }
        />

        <Section
          n="08"
          title="Changes to these terms"
          body={
            <p>
              We may update these terms; if we do, we'll bump the "last
              updated" date at the top and — for material changes — notify
              registered users by email. Continued use after a change is your
              agreement to the new terms.
            </p>
          }
        />

        <Section
          n="09"
          title="Contact"
          body={
            <p>
              Questions? Bugs? Legal threats? Use the{" "}
              <Link to="/contact" className="text-indigo-600 underline hover:text-indigo-800">contact form</Link>.
              A real person reads them.
            </p>
          }
        />

        <div className="mt-16 rounded-2xl tg-gradient-soft border border-indigo-100 p-6 text-sm text-slate-700">
          <strong className="text-slate-950">TL;DR:</strong> Use the app. Don't
          do crimes. Your data is yours; we keep it 12 months after you leave;
          export anytime. We're a small team that ships fast — if something's
          off, email us and we'll likely fix it tomorrow.
        </div>
      </article>

      <PublicFooter />
      <HaveAQuestionBadge />
    </div>
  );
}

function Section({ n, title, body }) {
  return (
    <section
      className="mt-12 pt-8 border-t border-slate-200"
      data-testid={`terms-section-${n}`}
    >
      <div className="flex items-baseline gap-3 mb-3">
        <span className="text-[11px] font-mono uppercase tracking-widest text-slate-400">
          §{n}
        </span>
        <h2 className="text-2xl text-slate-950">{title}</h2>
      </div>
      <div className="space-y-3 text-base text-slate-700 leading-relaxed">{body}</div>
    </section>
  );
}
